/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useEffect, useRef } from "react";

export const useJitterInterval = (
  callback: VoidFunction,
  delay: number | null,
  variance: number
): void => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!delay) {
      return;
    }

    let timeout: NodeJS.Timeout | null = null;
    const scheduleNextInterval = (): void => {
      const jitter = (Math.random() * 2 - 1) * variance;
      timeout = setTimeout(() => {
        callbackRef.current();
        scheduleNextInterval();
      }, jitter + delay);
    };

    scheduleNextInterval();

    return (): void => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
    };
  }, [delay, variance]);
};
